import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useSnackbar } from 'notistack';
import { Link, useHistory, useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import PricingRuleForm from '../../components/PricingRuleForm';
import Dialog from '@afosto/app-components-react/Dialog';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import { pricing as pricingApi } from '@afosto/order/api/Pricing';
import translations from './translations';

const formatAdjustments = (adjustments = []) =>
  adjustments.reduce((acc, adjustment) => {
    const {
      reference,
      description,
      amount,
      is_percentage: isPercentage,
      is_discount: isDiscount,
    } = adjustment;

    return [
      ...acc,
      {
        reference,
        description,
        amount,
        isPercentage,
        isDiscount,
      },
    ];
  }, []);

const EditPricingRule = () => {
  usePrivate();

  const intl = useIntl();
  const history = useHistory();
  const { id: pricingRuleID } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [pricingRule, setPricingRule] = useState(null);
  const [isFetchingPricingRule, setIsFetchingPricingRule] = useState(true);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchPricingRule = async id => {
      try {
        const response = await pricingApi.getPricingRule(id);
        const { data = {} } = response;

        if (isMounted) {
          setPricingRule({
            ...data,
            adjustments: formatAdjustments(data?.adjustments),
          });
        }
      } catch (error) {
        if (isMounted) {
          setPricingRule(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingPricingRule(false);
        }
      }
    };

    if (pricingRuleID) {
      fetchPricingRule(pricingRuleID);
    } else {
      setIsFetchingPricingRule(false);
    }

    return () => {
      isMounted = false;
    };
  }, [pricingRuleID]);

  const handleUpdatePricingRule = async ({
    name,
    constraints,
    filters,
    adjustments,
    applyTo,
    grantLimit,
    priority,
    activeAt,
    expiresAt,
    isLastRule,
  }) => {
    try {
      await pricingApi.updatePricingRule(pricingRule.id, {
        name,
        constraints,
        filters,
        adjustments,
        apply_to: applyTo,
        grant_limit: grantLimit,
        priority,
        active_at: activeAt,
        expires_at: expiresAt,
        is_last_rule: isLastRule,
      });

      enqueueSnackbar(intl.formatMessage(translations.feedback.pricingRuleUpdated), {
        variant: 'success',
      });

      history.push('/pricing-rules');
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.pricingRuleNotUpdated), {
        variant: 'error',
      });
    }
  };

  /**
   * Handle cancel of the pricing rule form.
   */
  const handleCancel = () => {
    history.push('/pricing-rules');
  };

  if (isFetchingPricingRule) {
    return <SkeletonPage fullWidth />;
  }

  if (!pricingRule) {
    return <h1>Pricing rule not found!</h1>;
  }

  return (
    <Page
      title={intl.formatMessage(translations.title)}
      breadcrumbs={[
        {
          label: intl.formatMessage(translations.breadcrumbs.pricingRules),
          path: '/pricing-rules',
        },
        {
          label: intl.formatMessage(translations.breadcrumbs.edit),
          path: `/pricing-rules/edit/${pricingRule.id}`,
          active: true,
        },
      ]}
      linkComponent={Link}
    >
      <PricingRuleForm
        pricingRule={pricingRule}
        onSubmit={handleUpdatePricingRule}
        onCancel={() => setShowConfirmCancel(true)}
      />
      <Dialog open={showConfirmCancel} onClose={() => setShowConfirmCancel(false)}>
        <DialogTitle>{intl.formatMessage(translations.cancelDialog.title)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage(translations.cancelDialog.description)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmCancel(false)} color="secondary">
            {intl.formatMessage(translations.cancelDialog.actions.cancel)}
          </Button>
          <Button onClick={handleCancel} color="primary" autoFocus>
            {intl.formatMessage(translations.cancelDialog.actions.ok)}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default EditPricingRule;
